import { Field, Form, Formik, FormikErrors } from "formik"
import useTranslation from "next-translate/useTranslation"
import Link from "next/link"
import { useDispatch } from "react-redux"
import { FormGroup } from "reactstrap"

import { FeedbackPostType, IDiscussion, IFeedbackPost, IProcess, IProject, IRI, IUser, ISupportRequest } from "@api/schema"
import { AvailableContentItems } from "@components/common/contentmanagement/ContentManagementHelper"
import { FormButtonArea } from "@components/common/form/FormButtonArea"
import FormikInput from "@components/common/form/FormikInput"
import GeneralFormError from "@components/common/form/GeneralFormError"
import IconRadioField from "@components/common/form/IconRadioField"
import { IconName } from "@components/common/Icon"
import TranslatedHtml from "@components/common/TranslatedHtml"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import { CUSTOMER_FEEDBACK_POST_TYPES } from "@customer-config"
import { addNotificationAction } from "@redux/actions/notifications"
import { createModelAction, IFormikActions } from "@redux/helper/actions"
import { EntityType } from "@redux/reduxTypes"
import { FeedbackHelper, FeedbackPostContent, ISinglePostData } from "@services/feedbackHelper"
import { useCurrentUser } from "@services/hooks/useCurrentUser"
import { NamespaceShortcut, prefixedKey, useDynamicTranslation } from "@services/i18n"
import { Routes, useLoginRouteWithRedirectBack, } from "@services/routes"
import { entityTypeFromIModelOrIRI, iriFromIModelOrIRI } from "@services/util"

const usedNamespaces: NamespaceShortcut[] = ["partner-market", "user"]

interface IProps {
  /** ID of any content of the relatedObject. Can be given, if a relatedObject is given */
  contentId?: AvailableContentItems
  /** if there is a Post, that should be answered. Either this or relatedObject must be set */
  parentPost?: IFeedbackPost
  /** URL for redirecting back after login */
  redirectBack?: string
  /** relatedObject to be commented. Either this or parentPost must be set */
  relatedObject?: IRI | IProject | IProcess | IUser | ISupportRequest
  /** function to be called, if the discussion/post was created successfull */
  onSuccessSubmit?: () => void
  /** function to be called, if the user hits the cancel-button */
  onCancel?: () => void
  /** pre defined type of the FeedbackPost. If set, the user will not be able to choose the type */
  presetFeedbackPostType?: FeedbackPostType
}

/**
 * This component provides a Form for a FeedbackPost
 * It triggers the creation of a new Discussion
 *
 * @todo: how to ensure, that at least parentPost OR relatedObject are given?
 *
 */
const SinglePostForm: React.FC<IProps> = (props: IProps) => {
  const { parentPost, contentId, onCancel, relatedObject, redirectBack, presetFeedbackPostType } = props
  const { t } = useTranslation("common")
  const td = useDynamicTranslation()
  const dispatch = useDispatch()
  const { currentUser, isAuthenticated } = useCurrentUser()

  const validate = (values: ISinglePostData): FormikErrors<ISinglePostData> => {
    const errors: { [key: string]: string } = {}

    if (values.content && values.content.length > 800) {
      errors.post = "validate.general.tooLong"
    }

    return errors
  }
  const loginWithRedirectBack = useLoginRouteWithRedirectBack(redirectBack)


  // Available post types in the default order.
  // May be replaced by CUSTOMER_FEEDBACK_POST_TYPES in the customer-config / settings.
  const availablePostTypes: FeedbackPostType[] = CUSTOMER_FEEDBACK_POST_TYPES as FeedbackPostType[]
    || [
      FeedbackPostType.Hint,
      FeedbackPostType.Criticism,
      FeedbackPostType.Question,
      FeedbackPostType.Support,
      FeedbackPostType.Troll,
    ]


  // use usecase dependent texts for the form
  let commentTitle = t("form.feedback.post.type.label")
  let commentDescription: string = null
  let salutation: JSX.Element = currentUser
    ? contentId
      ? <TranslatedHtml content="feedback.discussion.anonymousFeedback.commentByUserOnContent" params={{ username: currentUser?.username, content: t(contentId + ".label") }} />
      : <TranslatedHtml content="feedback.discussion.anonymousFeedback.answersByUser" params={{ username: currentUser?.username }} />
    : <p>{t("feedback.discussion.anonymousFeedback.loginToParticipate")}</p>
  let contentHelpText = t("form.feedback.post.comment.help")
  let contentLabelText = t("form.feedback.post.comment.label")
  let contentPlaceholderText = t("form.feedback.post.comment.placeholder")
  let submitButtonText = t("form.feedback.send")
  let submitButtonIcon: IconName = "comment"
  let toastSuccessMessage = t("message.discussion.created")

  switch (entityTypeFromIModelOrIRI(relatedObject)) {
    // @todo: individualize toast messages for other entitytypes
    case EntityType.SupportRequest:
      commentTitle = ""
      salutation = currentUser ? td("user", "helloUser", { username: currentUser?.username }) : null
      commentDescription = currentUser ? td("partner-market", "supportOffer.form.help") : td("partner-market", "supportOffer.form.helpBeforeLogin")
      contentHelpText = ""
      contentLabelText = ""
      contentPlaceholderText = td("partner-market", "supportOffer.form.placeholder")
      submitButtonText = td("partner-market", "supportOffer.form.submitButton")
      submitButtonIcon = "partner"
      // prefix the key to let the dynamicTranslation to the translation in/for the ToastContainer
      toastSuccessMessage = prefixedKey("partner-market", "supportOffer.create.created")
      break
  }


  const onFormSubmit = (values: ISinglePostData, actions: IFormikActions): void => {
    // only update those information provided by this form, let other data untouched
    actions.onSuccess = () => {
      dispatch(addNotificationAction(toastSuccessMessage, "success"))
      if (props.onSuccessSubmit) {
        props.onSuccessSubmit()
      }
    }

    // creating the feedbackpost from our input values as ISinglePostData
    const feedbackPost = parentPost
      ? FeedbackHelper.createNewFeedbackPostFromParentPost(parentPost)
      : FeedbackHelper.createNewFeedbackPostFromRelatedObject(iriFromIModelOrIRI(relatedObject), contentId)
    // transform the string-array from the Form (NewIFeedbackPost.newContent) to a content-Object for IFeedbackPost
    const content: FeedbackPostContent = {}
    content[0] = values.content

    // create a new Discussion, if a relatedObject is given
    if (relatedObject) {
      const newPost = {
        ...feedbackPost,
        content,
        type: values.type
      }
      const newDiscussion: IDiscussion = {
        contentId,
        initialPost: newPost,
        relatedObject: iriFromIModelOrIRI(relatedObject)
      }
      dispatch(createModelAction(EntityType.Discussion, newDiscussion, actions))
    }
    else if (parentPost) {
      // if a parentPost is given, create a new FeedbackPost
      // without new discussion
      const newPost: IFeedbackPost = {
        ...feedbackPost,
        content,
        type: values.type
      }
      dispatch(createModelAction(EntityType.FeedbackPost, newPost, actions))
    }
  }


  return <Formik<ISinglePostData>
    initialValues={{
      content: "",
      type: presetFeedbackPostType
    }}
    onSubmit={onFormSubmit}
    validate={validate}
    validateOnBlur={false}
    validateOnChange={false}
  >
    {({
      errors,
      handleSubmit,
      isSubmitting,
      setFieldValue,
      values,
    }) => <Form className="common-form feedback-post__answer__form" onSubmit={handleSubmit}>
        <div>
          {commentTitle && <h5>{commentTitle}</h5>}
          <p>
            {salutation}
          </p>
          <p>
            {commentDescription}
          </p>
        </div>
        <div>
          {isAuthenticated
            ? <>
              {// if the type of the post is not given, the user should choose
                !presetFeedbackPostType && <>
                  <p className="text-muted small">{t("form.feedback.post.type.placeholder")}</p>
                  <FormGroup name="commentType" role="group" aria-labelledby="checkbox-group" className="feedback-post__comment-type-container">
                    <div className="element-flex-layout">
                      {availablePostTypes.map(postType =>
                        <IconRadioField
                          key={postType}
                          name="type"
                          value={postType}
                          chosen={postType === values.type}
                          icon={FeedbackHelper.feedbackPostIcon(postType)}
                          text={t("feedback.post.type." + postType)}
                          tooltipText={t("feedback.post.type." + postType)}
                          setFieldValue={setFieldValue}
                          className="feedback-post__comment-type"
                        />
                      )}
                    </div>
                  </FormGroup>
                </>
              }
              {
                values.type && <>
                  <Field // comment-content
                    component={FormikInput}
                    name={"content"}
                    help={contentHelpText}
                    label={contentLabelText}
                    placeholder={contentPlaceholderText}
                    maxLength={800}
                    required
                    type="textarea"
                    value={values.content}
                  />

                  <p className="text-danger">{t("form.requiredFieldsHint")}</p>

                  <div className="text-right">
                    <GeneralFormError
                      errors={errors}
                      prefix="feedback"
                      excludeFields={["post", "type"]}
                    />
                    <FormButtonArea
                      isSubmitting={isSubmitting}
                      submitButton={{
                        title: submitButtonText,
                        iconName: submitButtonIcon
                      }}
                      cancelButton={{ onClick: onCancel }}
                    />
                  </div>
                </>
              }
            </>
            : <div className="btn-flex-layout mt-4 mb-4">
              <Link href={loginWithRedirectBack}>
                <a className="btn primary-btn">{t("goto.login")}</a>
              </Link>
              <Link href={Routes.Registration}>
                <a className="btn primary-btn">{t("goto.registration")}</a>
              </Link>
            </div>

          }
        </div >
      </Form >
    }
  </Formik >
}

export default withDynamicNamespaces<IProps>(SinglePostForm, usedNamespaces)